import { HttpClient } from '@angular/common/http';
import { Component, Inject } from '@angular/core';
import { SalesDocumentType, PersonsService, SalesDocument, DocumentStatus } from '@icc/b2b/shared';
import { ConfigService, DynamicElementsService, logger } from '@icc/shared';
import { DialogRef, DIALOG_DATA } from '@icc/ui';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { DocumentManagerService } from '../document-manager.service';
import { SafeHtml } from '@angular/platform-browser';
import { SalesDocumentService } from '../sales-document.service';
import { SyncService } from '../sync.service';
import { getSalesDocument } from '../+state/sales-documents.selectors';
import { isDepartmentIncludedInOrderIntegration } from '../order-integration-departments';
@Component({
  selector: 'icc-create-order-dialog',
  templateUrl: './create-order-dialog.component.html',
  styleUrls: ['./create-order-dialog.component.scss']
})
export class CreateOrderDialogComponent{
    orderLater = true;
    departments: Record<number, { name: string; priority: number }> = {};
    canBeOrdered = false;
    orderInProgress = false;

    orders: SalesDocument[] = [];
    salesOrder: SalesDocument;
    buyOrder: SalesDocument;
    documentIdToSendEmail: string | null = null;

    message = '';
    messages: string[] = [];
    messageState: 'default' | 'error' = 'default';

    estimatedDeadlineInfo: SafeHtml;
    estimatedDeadlineText: string;

    deliveryDates: string[] = [];
    deliveryDate: string | null = null;
    updatingDeliveryDates = false;

    placedUnexpectedError = false;
    hasOrderIntegration = this.configService.user.orderIntegration === 'OrdersSynchronization'
        && this.hasIntegrationActiveDocs();
    canOrderLater = this.configService.config.config.Trade.enableOrderLater;
    documentStatuses: DocumentStatus[] = [];

    messagesDict = {
        CREATING_ORDER: this.translateService.instant('SALES_DOCUMENT|Tworzenie zlecenia w systemie zewnętrznym.'),
        CREATE_ORDER_ERROR: this.translateService.instant('SALES_DOCUMENT|Zlecenie w systemie zewnętrznym nie zostało utworzone dla oferty.'),
        MISSING_DELIVERY_DATE: this.translateService.instant('SALES_DOCUMENT|Nie wybrano daty dostawy.'),
        PLACE_ORDER_FAILED: this.translateService.instant('SALES_DOCUMENT|UWAGA! Zamówienie zakupowe nie zostało złożone u producenta. Jeśli ponowienie zamówienia okaże się nieskuteczne, skontaktuj się z producentem poza systemem aby ustalić dalszy tok postępowania.'),
        PLACE_ORDER_FAILED_TIMEOUT: this.translateService.instant('SALES_DOCUMENT|Brak potwierdzenia zapisu zamówienia, oczekuj na powiadomienie email o stanie transakcji. W przypadku braku powiadomienia w ciągu 24h skontaktuj się ze swoim opiekunem klienta w celu wyjaśnienia sprawy.'),
        PLACE_ORDER_IN_PROGRESS: this.translateService.instant('SALES_DOCUMENT|Trwa składanie zamówienia...'),
        WITHDRAWING_ORDER: this.translateService.instant('SALES_DOCUMENT|Anulowanie składania zlecenia...'),
        WITHDRAW_ORDER_FAILED: this.translateService.instant('SALES_DOCUMENT|Anulowanie składania zlecenia nie powiodło się.'),
    };

    // eslint-disable-next-line max-params
    constructor(
        @Inject(DIALOG_DATA)
        public data: {
            document: SalesDocument;
            buyDocuments: SalesDocument[];
            documentIdToSendEmail?: string | null;
            order: (orderLater: boolean, deliveryDate: string) => Promise<{
                success: boolean;
                orders: SalesDocument[];
                documentIdToSendEmail: string | null;
                integration: boolean;
                messages?: string[];
                error?: string;
            }>;
            placeOrder: (orders: SalesDocument[], deliveryDate: string, documentIdToSendEmail: string) => Promise<{
                success: boolean;
                orders: SalesDocument[];
                documentIdToSendEmail: string | null;
                integration: boolean;
                messages?: string[];
                error?: string;
                inactiveProducts?: string[];
            }>;
            confirm: (orderLater: boolean, deliveryDate: string) => Promise<{
                success: boolean;
                orders: SalesDocument[];
                documentIdToSendEmail: string | null;
                integration: boolean;
                messages?: string[];
                error?: string;
            }>;
            withdrawPlaceOrders: () => Promise<{
                success: boolean;
            }>;
            onlyPlace?: boolean;
        },
        private translateService: TranslateService,
        private dialogRef: DialogRef,
        private personsService: PersonsService,
        private permissionsService: NgxPermissionsService,
        private http: HttpClient,
        private configService: ConfigService,
        private documentManagerService: DocumentManagerService,
        private dynamicElementsService: DynamicElementsService,
        private syncService: SyncService,
        private salesDocumentService: SalesDocumentService,
    ) {
        this.loadDepartments();

        this.estimatedDeadlineText =  this.dynamicElementsService.getElementContentBySlug('estimated-deadline-text', true);
        this.estimatedDeadlineInfo = this.dynamicElementsService.getElementHtmlContentBySlug('estimated-deadline-info');

        this.init();
    }

    async init() {
        this.orderLater = !(await this.permissionsService.hasPermission('purchase-make-order')) && this.data.buyDocuments.length > 0 || +this.data.document.sellerCompanyId !== +this.configService.user.user.ActiveCompany;
        if (this.hasOrderIntegration) {
            await this.requestExternalId();
        } else {
            this.canBeOrdered = true;
        }

        if (this.data.document.isOrderPlaced && this.data.buyDocuments.length > 0) {
            this.orders = this.data.buyDocuments;
            this.documentIdToSendEmail = this.data.documentIdToSendEmail;
        }
    }
    loadStatus() {
        this.documentStatuses =
            this.configService.user.statusSystems[
                (this.salesOrder ?? this.buyOrder)?.salesDocumentTypeId
            ]?.statuses ?? [];
    }

    private hasIntegrationActiveDocs(): boolean {
        const departments = this.configService.user.orderIntegrationDepartments;
        if (!departments?.length) return true;
        const docs = this.data.buyDocuments.length > 0 ? this.data.buyDocuments : [this.data.document];
        return docs.some((doc) =>
            isDepartmentIncludedInOrderIntegration(departments, doc.sellerProductDepartmentId)
        );
    }

    async requestExternalId() {
        const allDocs = this.data.buyDocuments.length > 0 ? this.data.buyDocuments : [this.data.document];
        const departments = this.configService.user.orderIntegrationDepartments;
        const docsWithIntegration = departments?.length
            ? allDocs.filter((doc) =>
                isDepartmentIncludedInOrderIntegration(departments, doc.sellerProductDepartmentId)
            )
            : allDocs;
        this.message = this.messagesDict.CREATING_ORDER;
        this.messages = [];
        this.messageState = 'default';
        this.updatingDeliveryDates = true;
        for (const document of docsWithIntegration) {
            try {
                const result = await this.http.post<{
                    externalId: string;
                    deliveryDates: string[];
                }>('/api/b2b/order-external/external-id', {
                    customerId: document.client?.companyVatNr,
                    documentId: document.uuid,
                    description: document.description,
                }).toPromise();
                if (result.externalId) {
                    document.externalId = result.externalId;
                    this.documentManagerService.saveDocument(document.uuid, { externalId: result.externalId });
                    if (!this.deliveryDates.length && result.deliveryDates?.length) {
                        this.deliveryDates = result.deliveryDates;
                        this.deliveryDate = this.deliveryDates[0];
                    }
                }
            } catch (e) {
                logger.error(e);
                this.updatingDeliveryDates = false;
                if (e.error?.messages?.length) {
                    this.message = '';
                    this.messages = e.error.messages;
                } else {
                    this.message = this.messagesDict.CREATE_ORDER_ERROR;
                    this.messages = [];
                }
                this.messageState = 'error';
                return;
            }
        }
        this.message = '';
        this.messages = [];
        this.canBeOrdered = true;
        this.updatingDeliveryDates = false;
    }

    close() {
        this.dialogRef.close();
    }

    async createOrder() {
        this.canBeOrdered = false;
        this.orderInProgress = true;
        this.messages = [];
        this.message = this.messagesDict.PLACE_ORDER_IN_PROGRESS;
        this.messageState = 'default';
        this.placedUnexpectedError = false;

        if (this.orderLater) {
            this.deliveryDate = null;
        } else {
            if (this.deliveryDates.length && !this.deliveryDate && this.hasOrderIntegration) {
                this.message = this.messagesDict.MISSING_DELIVERY_DATE;
                this.messageState = 'error';
                this.orderInProgress = false;
                return;
            }
        }

        if (!this.data.document.isConfirmed && this.data.document.salesDocumentTypeId === SalesDocumentType.Order) {
            const result = await this.data.confirm(this.orderLater, this.deliveryDate);
            if (result.success) {
                this.message = '';
                this.orderInProgress = false;
                this.dialogRef.close();
            } else {
                this.canBeOrdered = true;
                this.orderInProgress = false;
                if (result.messages?.length) {
                    this.messages = result.messages;
                    this.placedUnexpectedError = true;
                } else if (result.error === 'ETIMEDOUT'){
                    this.message = this.messagesDict.PLACE_ORDER_FAILED_TIMEOUT;
                } else {
                    this.message = this.messagesDict.PLACE_ORDER_FAILED;
                }
                this.messageState = 'error';
                this.orders = result.orders;
                this.documentIdToSendEmail = result.documentIdToSendEmail;
                if (result.integration && this.messageState === 'error') {
                    this.updateDeliveryDates();
                }
            }
        } else if (this.data.document.isOrderPlaced && this.orders.length) {
            if (!this.orderLater) {
                const result = await this.data.placeOrder(this.orders, this.deliveryDate, this.documentIdToSendEmail);
                if (result?.success) {
                    this.message = '';
                    this.messages = [];
                    this.dialogRef.close();
                    this.orderInProgress = false;
                } else {
                    this.canBeOrdered = true;
                    this.orderInProgress = false;
                    this.placedUnexpectedError = true;
                    if (result.inactiveProducts?.length) {
                        this.messages = [this.translateService.instant('SALES_DOCUMENT|W zamówieniu znajdują się produkty, których nie można zamówić: {products}', { products: result.inactiveProducts.join(', ') })];
                    } else if (result.messages?.length) {
                        this.messages = result.messages;
                    } else if (result.error === 'ETIMEDOUT'){
                        this.message = this.messagesDict.PLACE_ORDER_FAILED_TIMEOUT;
                    } else {
                        this.message = this.messagesDict.PLACE_ORDER_FAILED;
                    }
                    this.orders = result.orders;
                    this.buyOrder = await this.syncService.getOrLoadDocuments(this.orders?.map(doc => doc.uuid), getSalesDocument);
                    this.salesOrder = await this.salesDocumentService.getSalesDocumentForBuyDocument(this.orders?.[0]?.uuid);
                    this.loadStatus();
                    this.messageState = 'error';
                    if (result.integration && this.messageState === 'error') {
                        this.updateDeliveryDates();
                    }
                }
            } else {
                this.dialogRef.close();
            }
        } else {
            const result = await this.data.order(this.orderLater, this.deliveryDate);
            if (result.success) {
                this.message = '';
                this.orderInProgress = false;
                this.dialogRef.close();
            } else {
                this.canBeOrdered = true;
                this.orderInProgress = false;
                this.placedUnexpectedError = true;
                if (result.messages?.length) {
                    this.messages = result.messages;
                } else if (result.error === 'ETIMEDOUT'){
                    this.message = this.messagesDict.PLACE_ORDER_FAILED_TIMEOUT;
                } else {
                    this.message = this.messagesDict.PLACE_ORDER_FAILED;
                }
                this.messageState = 'error';
                this.orders = result.orders;
                this.buyOrder = await this.syncService.getOrLoadDocuments(this.orders?.map(doc => doc.uuid), getSalesDocument);
                this.salesOrder = await this.salesDocumentService.getSalesDocumentForBuyDocument(this.orders?.[0]?.uuid);
                this.loadStatus();
                this.documentIdToSendEmail = result.documentIdToSendEmail;
                if (result.integration && this.messageState === 'error') {
                    this.updateDeliveryDates();
                }
            }
        }
    }

    async loadDepartments() {
        await this.personsService.loaded;
        this.departments = this.personsService.getDepartments();
    }

    getDepartmentName(departmentId: number): string {
        const department = this.departments?.[departmentId];
        if (!department) return '';
        return typeof department === 'string' ? department : department.name;
    }

    async updateDeliveryDates() {
        const document = this.data.buyDocuments.length > 0 ? this.data.buyDocuments[0] : this.data.document;
        this.updatingDeliveryDates = true;
        try {
            const result = await this.http.post<{
                deliveryDates: string[];
            }>('/api/b2b/order-external/get-delivery-dates', {
                customerId: document.client?.companyVatNr,
                documentId: document.uuid,
                documentExternalId: document.externalId,
                description: document.description,
            }).toPromise();
            if (result.deliveryDates?.length) {
                this.deliveryDates = result.deliveryDates;
                this.deliveryDate = this.deliveryDates[0];
                this.canBeOrdered = true;
                this.updatingDeliveryDates = false;
            }
        } catch (e) {
            logger.error(e);
            if (e.error?.messages?.length) {
                this.messages = e.error.messages;
            }
            this.messageState = 'error';
            this.updatingDeliveryDates = false;
        }
    }

    async resignOrder() {
        this.message = this.messagesDict.WITHDRAWING_ORDER;
        this.messageState = 'default';
        const result = await this.data.withdrawPlaceOrders();
        if (result.success) {
            this.dialogRef.close();
        } else {
            this.message = this.messagesDict.WITHDRAW_ORDER_FAILED;
            this.messageState = 'error';
        }
    }

    getStatus(document: SalesDocument): DocumentStatus | undefined {
        return this.documentStatuses.find((el) => Number(el.id) === Number(document?.statusId));
    }
}
